import { IdeaItem } from "@/components/atoms/IdeaItem";
import { PageItem } from "@/components/atoms/PageItem";
import { Button } from "@/components/ui/button";
import type { Idea, Page } from "@/schemas";

interface SearchResultsDropdownProps {
  pages: Page[];
  ideas: Idea[];
  domainId: string;
  searchQuery: string;
  handleSelectPage: (domainId: string, pageId: string) => void;
  handleSelectIdea: (domainId: string, ideaId: string) => void;
  /** Omitted for viewers — adding a manual page is admin-only. */
  handleCreateContext?: () => void;
}

export function SearchResultsDropdown({
  pages,
  ideas,
  domainId,
  searchQuery,
  handleSelectPage,
  handleSelectIdea,
  handleCreateContext,
}: SearchResultsDropdownProps) {
  const hasResults = pages.length > 0 || ideas.length > 0;
  const isSearching = searchQuery.trim().length > 0;

  return (
    <div className="absolute top-full left-0 right-0 mt-1 z-10 max-h-[min(32rem,calc(100vh-8rem))] overflow-y-auto rounded-xl border border-border bg-background shadow-xl">
      {hasResults ? (
        <div className="p-2">
          {pages.map((page) => (
            <PageItem
              key={page.id}
              page={page}
              domainId={domainId}
              handleSelectPage={handleSelectPage}
            />
          ))}

          {pages.length > 0 && ideas.length > 0 && (
            <div className="my-2 border-t border-dashed border-border/80" />
          )}

          {ideas.map((idea) => (
            <IdeaItem
              key={idea.id}
              idea={idea}
              domainId={domainId}
              handleSelectIdea={handleSelectIdea}
            />
          ))}
        </div>
      ) : (
        <div className="p-4">
          <p className="text-sm text-muted-foreground mb-2">
            {isSearching
              ? "No matching pages or ideas found"
              : "No pages or ideas yet for this website"}
          </p>
          {isSearching && handleCreateContext && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleCreateContext}
            >
              Add &ldquo;{searchQuery}&rdquo; as a manual page
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
